import { Box } from "@radix-ui/themes";
import React, { useState, useCallback } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { twMerge } from "tailwind-merge";
import { useDebouncedPostHogCapture } from "@/shared/hooks/useDebouncedPostHogCapture";

interface ZoomPanWrapperProps {
  children: React.ReactNode;
  className?: string;
}

interface ZoomEvent {
  state: {
    scale: number;
    previousScale: number;
    positionX: number;
    positionY: number;
  };
}

const activationKey = "Control";

export function ZoomPanWrapper({ children, className }: ZoomPanWrapperProps) {
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [isZooming, setIsZooming] = useState(false);
  const captureDebounced = useDebouncedPostHogCapture();

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === activationKey && !event.repeat) {
      setIsShiftPressed(true);
    }
  }, []);

  const handleKeyUp = useCallback((event: React.KeyboardEvent) => {
    if (event.key === activationKey) {
      setIsShiftPressed(false);
    }
  }, []);

  const handleBlur = useCallback(() => {
    setIsShiftPressed(false);
    setIsPanning(false);
    setIsZooming(false);
  }, []);

  const handlePanningStart = useCallback(() => {
    captureDebounced("workspace_pan_start");
    setIsPanning(true);
  }, [captureDebounced]);

  const handlePanningStop = useCallback(() => {
    captureDebounced("workspace_pan_stop");
    setIsPanning(false);
  }, [captureDebounced]);

  const handleZoomStart = useCallback(
    (e: ZoomEvent) => {
      const { scale, previousScale } = e.state;
      const direction = scale > previousScale ? "in" : "out";

      captureDebounced(`workspace_zoom_${direction}_start`, {
        scale,
        previousScale,
      });
      setIsZooming(true);
    },
    [captureDebounced]
  );

  const handleZoomStop = useCallback(
    (e: ZoomEvent) => {
      const { scale, previousScale } = e.state;

      captureDebounced("workspace_zoom_stop", {
        scale,
        previousScale,
      });
      setIsZooming(false);
    },
    [captureDebounced]
  );

  const getCursor = () => {
    if (isPanning) return "grabbing";
    if (isZooming) return "zoom-in";
    if (isShiftPressed) return "grab";
    return "inherit";
  };

  return (
    <Box
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onBlur={handleBlur}
      className="outline-none w-full h-full"
      style={{
        cursor: getCursor(),
      }}
    >
      <TransformWrapper
        initialScale={1}
        minScale={1}
        maxScale={5}
        disablePadding
        panning={{
          velocityDisabled: true,
          activationKeys: [activationKey],
        }}
        wheel={{
          step: 5,
          smoothStep: 0.003,
          activationKeys: [activationKey],
        }}
        doubleClick={{ disabled: true }}
        onPanningStart={handlePanningStart}
        onPanningStop={handlePanningStop}
        onZoomStart={handleZoomStart}
        onZoomStop={handleZoomStop}
      >
        <TransformComponent
          wrapperClass={twMerge([
            "w-full h-full flex items-center justify-center",
            className,
          ])}
          contentClass="w-full h-full flex items-center justify-center"
          wrapperStyle={{
            cursor: getCursor(),
          }}
        >
          {children}
        </TransformComponent>
      </TransformWrapper>
    </Box>
  );
}
